import React, { useState, useEffect, useRef, useCallback } from 'react';

interface ImageViewerProps {
  images: string[];
  initialImage?: string;
}

interface TouchState {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  isDragging: boolean;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ images, initialImage }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [touchState, setTouchState] = useState<TouchState>({
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    isDragging: false
  });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialImage) {
      const initialIndex = images.findIndex(img => img.includes(initialImage));
      if (initialIndex !== -1) {
        setCurrentIndex(initialIndex);
      }
    }
  }, [images, initialImage]);



  const goToNext = useCallback(() => {
    if (isTransitioning) return;

    setIsTransitioning(true);
    setSlideDirection('right');
    setCurrentIndex(prevIndex => (prevIndex + 1) % images.length);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  }, [isTransitioning, images.length]);

  const goToPrev = useCallback(() => {
    if (isTransitioning) return;

    setIsTransitioning(true);
    setSlideDirection('left');
    setCurrentIndex(prevIndex => (prevIndex - 1 + images.length) % images.length);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  }, [isTransitioning, images.length]);

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning || index === currentIndex) return;

    setIsTransitioning(true);
    setSlideDirection(index > currentIndex ? 'right' : 'left');
    setCurrentIndex(index);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  }, [isTransitioning, currentIndex]);

  const prevIndex = (currentIndex - 1 + images.length) % images.length;
  const nextIndex = (currentIndex + 1) % images.length;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStartNative = (e: TouchEvent) => {
      if (isTransitioning) return;

      const touch = e.touches[0];
      setTouchState({
        startX: touch.clientX,
        startY: touch.clientY,
        currentX: touch.clientX,
        currentY: touch.clientY,
        isDragging: true
      });
    };

    const handleTouchMoveNative = (e: TouchEvent) => {
      if (!touchState.isDragging || isTransitioning) return;

      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchState.startX);
      const deltaY = Math.abs(touch.clientY - touchState.startY);

      if (deltaX > deltaY) {
        e.preventDefault();
      }

      setTouchState(prev => ({
        ...prev,
        currentX: touch.clientX,
        currentY: touch.clientY
      }));
    };

    const handleTouchEndNative = () => {
      if (!touchState.isDragging || isTransitioning) return;

      const deltaX = touchState.currentX - touchState.startX;
      const deltaY = Math.abs(touchState.currentY - touchState.startY);
      const threshold = 50;

      setTouchState(prev => ({ ...prev, isDragging: false }));

      if (Math.abs(deltaX) > threshold && Math.abs(deltaX) > deltaY) {
        if (deltaX > 0) {
          goToPrev();
        } else {
          goToNext();
        }
      }
    };

    container.addEventListener('touchstart', handleTouchStartNative, { passive: true });
    container.addEventListener('touchmove', handleTouchMoveNative, { passive: false });
    container.addEventListener('touchend', handleTouchEndNative, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStartNative);
      container.removeEventListener('touchmove', handleTouchMoveNative);
      container.removeEventListener('touchend', handleTouchEndNative);
    };
  }, [touchState.isDragging, isTransitioning, touchState.startX, touchState.startY, touchState.currentX, touchState.currentY, goToPrev, goToNext]);

  return (
    <div className="image-viewer">
      <div
        className="carousel-container"
        ref={containerRef}
      >
        <div className="image-container">
          <div className="side-image left-image">
            <img 
              src={`/assets/lumina/${images[prevIndex]}`} 
              alt="Previous" 
              className="blurred-image"
            />
          </div>
          
          <div 
            className={`center-image ${isTransitioning ? 
              (slideDirection === 'right' ? 'slide-right' : 'slide-left') : ''}`}
          >
            <img 
              src={`/assets/lumina/${images[currentIndex]}`} 
              alt="Current" 
              className={isTransitioning ? "transitioning" : ""}
            />
          </div>
          
          <div className="side-image right-image">
            <img 
              src={`/assets/lumina/${images[nextIndex]}`} 
              alt="Next" 
              className="blurred-image"
            />
          </div>
        </div>
        
        <button 
          className="nav-button prev-button" 
          onClick={goToPrev}
          aria-label="Previous image"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <button 
          className="nav-button next-button" 
          onClick={goToNext}
          aria-label="Next image"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
      
      <div className="pagination">
        {images.map((_, index) => (
          <span 
            key={index} 
            className={`dot ${index === currentIndex ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageViewer; 